import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Service } from '@/types/Service';
import { servicesApi } from '@/services/api';
import { ServiceCard } from './ServiceCard';
import { ServiceForm } from './ServiceForm';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/responsive-modal';
import { useToast } from '@/hooks/useToast';
import { CardTitle } from '@/components/ui/card';

interface ServicesManagerProps {
  agentId: number;
  /** When provided with onServicesChange, skips mount fetch and uses parent cache */
  services?: Service[];
  isLoading?: boolean;
  onServicesChange?: (services: Service[]) => void;
  crmEnabled?: boolean;
}

export const ServicesManager: React.FC<ServicesManagerProps> = ({
  agentId,
  services: servicesProp,
  isLoading: isLoadingProp,
  onServicesChange,
  crmEnabled = false,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isManagedExternally = onServicesChange != null;
  const [services, setServices] = useState<Service[]>(servicesProp ?? []);
  const [isLoading, setIsLoading] = useState(
    isManagedExternally ? Boolean(isLoadingProp) : false
  );
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (isManagedExternally) return;
    void fetchServices();
  }, [agentId, isManagedExternally]);

  useEffect(() => {
    if (!isManagedExternally || servicesProp === undefined) return;
    setServices(servicesProp);
  }, [isManagedExternally, servicesProp]);

  useEffect(() => {
    if (!isManagedExternally || isLoadingProp === undefined) return;
    setIsLoading(isLoadingProp);
  }, [isManagedExternally, isLoadingProp]);

  const fetchServices = async () => {
    if (!isManagedExternally) setIsLoading(true);
    try {
      const data = await servicesApi.fetchServices(agentId);
      if (isManagedExternally) {
        onServicesChange?.(data);
      } else {
        setServices(data);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.services.errors.fetch_failed.translation',
          'Failed to Load Services'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    } finally {
      if (!isManagedExternally) setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedService(null);
    setIsFormOpen(true);
  };

  const handleEdit = (service: Service) => {
    setSelectedService(service);
    setIsFormOpen(true);
  };

  const handleDelete = (serviceId: number) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setServiceToDelete(service);
      setIsDeleteDialogOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!serviceToDelete) return;

    try {
      await servicesApi.deleteService(serviceToDelete.id);
      toast({
        title: t('agent.services.deleted.translation', 'Service Deleted'),
      });
      await fetchServices();
      setIsDeleteDialogOpen(false);
      setServiceToDelete(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.services.errors.delete_failed.translation',
          'Delete Failed'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    }
  };

  const handleSave = async (
    serviceData:
      | Parameters<typeof servicesApi.createService>[0]
      | Parameters<typeof servicesApi.updateService>[1]
  ) => {
    try {
      if (selectedService) {
        await servicesApi.updateService(
          selectedService.id,
          serviceData as Parameters<typeof servicesApi.updateService>[1]
        );
      } else {
        await servicesApi.createService(
          serviceData as Parameters<typeof servicesApi.createService>[0]
        );
      }
      await fetchServices();
      setIsFormOpen(false);
      setSelectedService(null);
    } catch (error) {
      throw error; // Let form handle the error toast
    }
  };

  const handleToggleEnabled = async (serviceId: number, enabled: boolean) => {
    try {
      await servicesApi.toggleServiceEnabled(serviceId, enabled);
      await fetchServices();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.services.errors.toggle_failed.translation',
          'Toggle Failed'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <CardTitle
            description={t(
              crmEnabled
                ? 'agent.services.helper.translation'
                : 'agent.services.helper_no_crm.translation',
              crmEnabled
                ? 'Manage the services your business offers to customers.'
                : 'Add services so the agent can answer pricing and duration questions.'
            )}
          >
            {t('agent.services.title.translation', 'Services')}
          </CardTitle>
        </div>
        <Button
          variant="brand"
          size="icon"
          onClick={handleCreate}
          className="shrink-0 lg:h-9 lg:w-auto lg:gap-2 lg:px-4 lg:py-2"
          aria-label={t('agent.services.add.translation', 'Add Service')}
        >
          <Plus className="h-4 w-4" />
          <span className="hidden lg:inline">
            {t('agent.services.add.translation', 'Add Service')}
          </span>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">
            {t('agent.services.loading.translation', 'Loading services...')}
          </div>
        </div>
      ) : services.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg bg-card py-12 text-center">
          <div className="mb-4 text-4xl text-muted-foreground">
            {t('agent.services.empty.translation', 'No services')}
          </div>
          <div className="mb-6 text-lg text-muted-foreground">
            {t(
              crmEnabled
                ? 'agent.services.empty_description.translation'
                : 'agent.services.empty_description_no_crm.translation',
              crmEnabled
                ? 'Add services to start managing appointments'
                : 'Add services so the agent can answer pricing and duration questions.'
            )}
          </div>
          <Button
            variant="brand"
            size="icon"
            onClick={handleCreate}
            className="lg:h-9 lg:w-auto lg:gap-2 lg:px-4 lg:py-2"
            aria-label={t('agent.services.add.translation', 'Add Service')}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden lg:inline">
              {t('agent.services.add.translation', 'Add Service')}
            </span>
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map(service => (
            <ServiceCard
              key={service.id}
              service={service}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleEnabled={handleToggleEnabled}
            />
          ))}
        </div>
      )}

      {/* Service Form Dialog */}
      <ServiceForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedService(null);
        }}
        onSave={handleSave}
        service={selectedService}
        agentId={agentId}
      />

      {/* Delete Confirmation */}
      <Modal open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <ModalContent className="w-full max-w-lg">
          <ModalHeader>
            <ModalTitle
              description={t(
                'agent.services.delete_confirm.description.translation',
                'Are you sure you want to delete "{name}"? This action cannot be undone.',
                { name: serviceToDelete?.name }
              )}
            >
              {t(
                'agent.services.delete_confirm.title.translation',
                'Delete Service'
              )}
            </ModalTitle>
          </ModalHeader>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setServiceToDelete(null);
              }}
            >
              {t('common.cancel.translation', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              {t('common.delete.translation', 'Delete')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
